<template>
  <div class="cpt-rich-text-editor" ref="cpt-rich-text-editor" :style="{'--tag-name-width': `${tagNameWidth}px`}">
    <div
      class="hb-editor-wrapper"
      :class="{'command-editor': isCommand}"
      ref="editor"
    ></div>
    <div class="placeholder text-ellipse" v-if="config.placeholder && (!content || (content === '<p></p>'))" v-html="config.placeholder"></div>
    <div class="max-num-wrapper" :class="{'overflow': inputLength > maxLength}" v-if="maxLength">
      {{ inputLength }}/{{ maxLength }}
    </div>
    <slot></slot>
    
    <SelectionPanel
      ref="selectionPanel"
      v-if="showSelectionPanel"
      :items="selectionList" 
      :pos="selectionPos"
      :toBody="!notSeclectionToBody"
      :isBottomLock="isPanelBottomLock"
      @close="handleSelectionPanelClose"
      @select="handleSelectionSelect"
      @scroll-bottom="handleSelectionScrollBottom"
    />

    <EditLinkDialog
      v-if="editLinkDialogConfig.show"
      :text="editLinkDialogConfig.text"
      :link="editLinkDialogConfig.link"
      :top="editLinkDialogConfig.top"
      :left="editLinkDialogConfig.left"
      :is_add="editLinkDialogConfig.is_add"
      @close="handleCloseEditLinkDialog"
      @update="handleLinkUpdate"
    ></EditLinkDialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import emojiRender from '_lib/emoji/EmojiRender'
import { calcUserName, calcUserRoleColor } from '_lib/CalcUserInfo'
import { getMatchedUserList } from '_api/room/index'
import { getFullChannelList } from "_api/channel";
import { getSimpleFriendList } from "_api/friend";
import { getGroupChatMemberList } from '_api/group-chat';
import { uploadFile } from '_lib/upload'
import privateRolesMixin from '_mixin/privateRole'
import { CHANNEL_TYPES } from '@/common/constants/channel';
import { DEFAULT_RICH_TEXT_CONFIG } from '_lib/constants/richInput'
import EventBus from "_lib/EventBus";
import { getEmojiHtml } from '@/directives/emojiHtmlDirective'
import { handleH12ToH23, htmlToHbEditorHtml } from '_lib/MarkdownTransfer'
import { base64ToFile, blobUrlToFile, httpUrlToFile } from '_lib/utils/utilsFile';
import { decimalToHexColor } from '_lib/utils/utilsColor';
import { replaceAngleBracket } from '_lib/purify';
import { BOT_COMMAND_OPTION_TYPES, BOT_COMMAND_OPTION_TYPES_NEED_UPLOAD } from '_lib/constants/bot';
import { GROUP_CHAT_ALL_MEMBER } from '@/common/constants/group-chat';
const SelectionPanel = () => import('./components/SelectionPanel.vue')
const EditLinkDialog = () => import('_com/form/EditLinkDialog.vue')
const EditLinkWidth = 316
const EditLinkHeight = 214
const HIDE_AT_ROLE_TYPE = [254]
class ImageFileInfo {
  file = null
  width = 0
  height = 0
  blobUrl = ''
  url = ''
  upload = null
  
  release() {
    URL.revokeObjectURL(this.blobUrl)
    this.upload = null
    this.file = null
  }
}
export default {
  components: { SelectionPanel, EditLinkDialog },
  props: {
    // post、post_comment、im、msg_edit、private_im、user_desc、channel_anc
    group: {
      type: String,
      default: ''
    },
    initValue: {
      type: String,
      default: ''
    },
    transformToHtml: {
      type: Boolean,
      default: true
    },
    config: {
      type: Object,
      default: () => DEFAULT_RICH_TEXT_CONFIG
    },
    isCommand: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number,
      default: 0
    },
    pastedImageMapper: {
      type: Object,
      default: () => ({})
    },
    inputType: {
      type: String,
      default: ""
    }
  },
  data () {
    return {
      showSelectionPanel: false,
      selectionType: '',
      selectionList: [],
      selectionPos: {},
      editor_modules: null,
      loadHbEditorPromise: null,
      content: '',
      editor: null,
      editLinkDialogConfig: {
        show: false,
        left: 0,
        top: 0,
        link: '',
        text: '',
        is_add: false,
      },
      inputLength: 0,
      notSeclectionToBody: false,
      insertedUsers: {},

      imgsAddTask: [], // 粘贴图片列表映射（同步上传图片）
      isPanelBottomLock: false,
      tagNameWidth: 12,

      uploadingIds: [],
    }
  },
  computed: {
    ...mapGetters([
      'channel_data',
      'cur_room_data', 
      'channels', 
      'cur_roles_list',
      'cur_room_bots',
      'user_card_setting',
      'cur_room_data_is_big',
      'cur_group_chat',
      'user_info',
    ]),
  },
  watch: {
    disabled (v) {
      if (!this.editor?.view) return
      if (v) {
        this.editor.view.setProps({
          editable: () => false
        })
        clearTimeout(this._show_channel_panel_timer)
        clearTimeout(this._show_at_panel_timer)
      } else {
        this.editor.view.setProps({
          editable: () => true
        })
      }
    },
    'channel_data.channel_id': {
      handler () {
        clearTimeout(this._show_channel_panel_timer)
        clearTimeout(this._show_at_panel_timer)
        if (this.showSelectionPanel) {
          this.showSelectionPanel = false
          this.cancelShowSelectionPanel = true
          this.selectionList = []
          this.selectionPos = {}
        }
      },
    },
    uploadingIds: {
      handler(v) {
        const isUploading = v.length > 0
        this.$emit('uploading-image', isUploading)
      }
    },
  },
  mixins: [ privateRolesMixin ], 
  created () {
    this.initEditorScripts()
  },
  mounted() {
    this.loadHbEditorPromise.then(() => {
      this.initEditor(this.editor_modules)
    })
    if (this.isCommand) {
      setTimeout(() => {
        const tagWrapper = this.$el.querySelector('.tag-wrapper')
        const tagValue = tagWrapper?.querySelector('.tag-value[contenteditable="true"]')
        const commandName = tagValue && this.$el.querySelector('.command-name')
        if (commandName) {
          this.tagNameWidth = commandName.offsetWidth + 20
        }
      })
    }
    EventBus.$on('HandleRichTextFormat', this.applyFormat)
    EventBus.$on('HandleRichToolBar', this.handleToolBarClick)
    EventBus.$on('HandleUpdateActiveTagContent', this.handleUpdateActiveTagContent)
  },
  beforeDestroy () {
    EventBus.$off('HandleRichTextFormat', this.applyFormat)
    EventBus.$off('HandleRichToolBar', this.handleToolBarClick)
    EventBus.$off('HandleUpdateActiveTagContent', this.handleUpdateActiveTagContent)
    this.handleSelectionPanelClose()
    if (this.editLinkDialogConfig?.show) {
      const el = document.querySelector('.edit-link-dialog')
      el && el.remove()
    }
    this.editLinkDialogConfig = {}
    this.editor && this.editor.destroy()
    clearTimeout(this._show_channel_panel_timer)
    clearTimeout(this._show_at_panel_timer)
  },
  methods: {
    initEditorScripts () {
      this.loadHbEditorPromise = new Promise((resolve, reject) => {
        if (this.editor_modules) {
          resolve(this.editor_modules)
        } else {
          import('@heybox/hb-editor').then(editor_modules => {
            this.editor_modules = editor_modules
            resolve(this)
          }).catch(() => {
            reject()
          })
        }
      })
    },
    ensureHbEditorLoaded () {
      if (this.loadHbEditorPromise) {
        return this.loadHbEditorPromise
      } else {
        return new Promise((resolve) => {
          resolve(this)
        })
      }
    },
    generateEditorModules (editor_modules) {
      const modules = [editor_modules.HBEditorModuleParagraph]
      if (this.isCommand) {
        modules.push(editor_modules.HBEditorModuleBot)
        modules.push(editor_modules.HBEditorModuleBotTextTag)
        this.config.at && modules.push(editor_modules.HBEditorModuleChatUser)
        this.config.channel && modules.push(editor_modules.HBEditorModuleChatChannel)
        return modules
      }
      if (this.config.markdown) {
        const markdown = this.config.markdown
        if (markdown.h1 || markdown.h2) {
          modules.push(editor_modules.HBEditorModuleHeading)
        }
        if (markdown.ol || markdown.ul) {
          modules.push(editor_modules.HBEditorModuleList)
        }
        modules.push(editor_modules.HBEditorModuleChatBot)
      }
      if (this.config.emoji) {
        modules.push(editor_modules.HBEditorModuleChatEmoji)
      }
      if (this.config.image) {
        modules.push(editor_modules.HBEditorModuleChatImage)
      }
      if (this.config.link) {
        modules.push(editor_modules.HBEditorModuleLink)
      }
      if (this.config.at) {
        modules.push(editor_modules.HBEditorModuleChatUser)
      }
      if (this.config.channel) {
        modules.push(editor_modules.HBEditorModuleChatChannel)
      }
      return modules
    },
    generateEditorPlugins (editor_modules) {
      if (this.isCommand) {
        const { Plugin, PluginKey } = editor_modules
        return [
          editor_modules.OnlyOneLinePlugin(),
          new Plugin({
            key: new PluginKey('updated'),
            view: () => {
              return {
                update: (view) => {
                  setTimeout(() => {
                    this.$emit('checkLackTags')
                  })
                },
              }
            }
          })
        ]
      }
      const PMPlugin = []
      if (this.config.lineBreak) {
        PMPlugin.push(editor_modules.BaseHotKeyPlugin(this.config.breakWay === 'shiftEnter' ? 'chat_line' : 'chat_article'))
      } else {
        PMPlugin.push(editor_modules.OnlyOneLinePlugin())
      }
      if (this.maxLength) {
        PMPlugin.push(
          editor_modules.WordsNumLimitPlugin((map) => {
            this.inputLength = map.text + (map.img_emoji || 0) + (map.chat_img || 0)
            this.$emit('wordsLimit', this.inputLength)
            return true
          }, { mode: 'other' })
        )
      }
      return PMPlugin
    },
    handlePasteImage (data) {
      if (this.group === 'msg_edit') {
        this.$emit('paste-image-change', data)
      } else {
        EventBus.$emit('paste-image-change', data)
      }
    },
    initEditor ({HBEditor, ...editor_modules}) {
      const modules = this.generateEditorModules(editor_modules)
      const PMPlugin = this.generateEditorPlugins(editor_modules)
      this.content = this.handleInitValue()
      this.editor = new HBEditor(
        this.$refs['editor'], 
        {
          content: this.isCommand ? '' : this.content,
          modules,
          PMPlugin,
          on: {
            handleInsertUser: data => {
              if (this.isCommand) {
                return
              }
              if (this.config.at) {
                clearTimeout(this._show_at_panel_timer)
                if (data.is_over || !this.showSelectionPanel) {
                  this.showPanel('at', data)
                } else {
                  this._show_at_panel_timer = setTimeout(() => {
                    this.showPanel('at', data)
                  }, 500)
                }
              }
            },
            handleInsertChannel: data => {
              if (this.config.channel) {
                clearTimeout(this._show_channel_panel_timer)
                if (data.is_over || !this.showSelectionPanel) {
                  this.showPanel('channel', data)
                } else {
                  this._show_channel_panel_timer = setTimeout(() => {
                    this.showPanel('channel', data)
                  }, 500)
                }
              }
            },
            handleBotCommand: (data) => {
              this.config.botCommand && this.$emit('botCommandChange', data)
            },
            handleAddChatImage: async (images) => {
              if (images.length > 0) {
                for (let i = 0; i < images.length; i++) {
                  const image = images[i]
                  await this.onRichImageAdd(image)

                  if (this.imgsUploadTask && this.imgsUploadTask.length !== 0) {
                    const task = this.imgsUploadTask.find(task => task.url === image.url)
                    task && task.resolve()
                  }
                }
              }
            },
            handleRemoveChatImage: (data) => {
              this.handlePasteImage({
                group: this.group,
                type: 'delete',
                key: data[0].key
              })
            },
            handleBotCommandChange: (data) => {
              if (this.isCommand) {
                this.$emit('checkBotCommand', {
                  ...data,
                  content: this.editor.modules.bot.bot_info.content
                })
              }
            },
            // handleBotMatchTagText: (data) => {
            //   console.log('[botMatchTagText]', data);
            // },
            handleTextTagInput: ({tag, intag}) => {
              this.$emit('commandTagChange', tag, intag)
            }
          },
          viewProps: {
            handleDOMEvents: {
              click: (view, event) => {
                event.preventDefault();
              },
              focus: () => {
                this.$emit('focus')
              },
              blur: () => {
                this.$emit('blur')
              },
              paste: (view, event) => {
                /*
                  INFO:
                  1、那就是纯图片就是粘贴到文本框（单图 多图 / 简单文本 富文本）
                  2、如果都是文件就按照飞书的方式弹窗确认发送
                  3、如果是文件和图片也是一样弹窗，图片不粘贴到文本框，和文件一样发送出去
                */

                // 私信、IM、活动、公告
                if (!['private_im', 'im', 'RoomActivity', 'room-anc'].includes(this.group)) return;

                const fileList = (event.clipboardData || event.originalEvent.clipboardData).files;
                if (fileList.length === 0) return;
                if (fileList.length > 0) event.preventDefault()
                
                const fileGroup = { files: [], images: [] }
                for (const file of fileList) {
                  const fileType = file.type;
                  const isImage = fileType.startsWith("image/")
                  isImage ? fileGroup.images.push(file) : fileGroup.files.push(file)
                }

                // 只存在图片
                if (fileGroup.images.length > 0 && fileGroup.files.length === 0) {
                  // INFO: 暂做保留
                  // if (fileGroup.images.length === 1) {
                  //   const file = fileGroup.images[0]
                  //   if (this.group === "im") {
                  //     EventBus.$emit("Im_Upload_Img", { target: { files: [file] } })
                  //   } else {
                  //     EventBus.$emit("Private_Im_Upload_Img", { target: { files: [file] } })
                  //   }
                  // } 

                  this.imgsUploadTask = fileGroup.images.reduce((acc, cur) => {
                    const info = {}
                    const promise = new Promise((resolve, reject) => {
                      info.resolve = resolve
                      info.reject = reject
                    })
                    info.promise = promise
                    info.img = cur
                    info.url = URL.createObjectURL(cur)
                    acc.push(info)
                    return acc
                  }, [])

                  const executeTasksSequentially = (tasks) => {
                    if (tasks.length === 0) return Promise.resolve();
                    const [firstTask, ...remainingTasks] = tasks;
                    this.addImageToEditor(firstTask.url, () => {
                      event.target.value = '';
                    });
                    return firstTask.promise.then(() => {
                      return executeTasksSequentially(remainingTasks);
                    });
                  }

                  executeTasksSequentially(this.imgsUploadTask).then(() => {
                    this.imgsUploadTask = []
                  });
                }

                // 只存在文件 / 存在文件和图片的组合
                if (fileGroup.files.length > 0) {
                  // 单文件上传
                  if(fileGroup.files.length === 1 && fileGroup.images.length === 0) {
                    const file = fileGroup.files[0]
                    if (this.group === 'private_im') {
                      // 判断是不是视频
                      const isVideo = file.type.startsWith('video/')
                      isVideo && EventBus.$emit("Private_Im_Upload_File", { target: { files: [file] }})
                    } else if(this.group === 'im') {
                      EventBus.$emit("Im_Upload_File", { target: { files: [file] }})
                    }
                  }

                  // 多文件上传
                  const filesList = [...fileGroup.files, ...fileGroup.images]
                  if (filesList.length > 1) {
                    // TODO: 私信过滤除视频以外的文件
                    const emitData = { target: { files: [...filesList] } }
                    if (this.group === 'private_im') {
                      const isVideo = file.type.startsWith('video/')
                      isVideo && EventBus.$emit("Private_Im_Upload_File_Group", emitData)
                    } else if(this.group === 'im') {
                      EventBus.$emit("Im_Upload_File_Group", emitData)
                    } else if (this.group === 'group-im') { 
                      EventBus.$emit("Group_Im_Upload_File_Group", emitData)
                    }
                  }
                }
              }
            }, 
            transformPastedHTML: (html, view) => {
              const newHtml = this.handleTransformPastedHTML(html)
              return newHtml
            },
            handleClickOn: (view, pos, node, nodePos, event) => {
              if (node.type.name === 'link') {
                if (this.config.userCard) {
                  const target = event.target
                  const classList = target.classList || []
                  if (classList.contains('hb-editor-chat-user') && !classList.contains('role-at')) {
                    const user_id = target.getAttribute('data-remind-user-id')
                    if (user_id != 0) {
                      event._currentTarget = target
                      user_id && EventBus.$emit('User_Menu:card', event, 'fixed_left', { user_id }, {key: '', offset: -4, origin: true});
                    }
                  }
                } else {
                  const target = event.target
                  const { top, left } = this.getLinkEditPos(target, 'bottom')
                  this.editLinkDialogConfig = {
                    show: true,
                    top,
                    left,
                    text: target.innerText,
                    link: target.getAttribute('href'),
                    is_add: false,
                    view,
                    node,
                    nodePos,
                    target
                  }
                }
              }
            },
            transformPastedText: (text, plain, view) => {
              return text
            },
            handleKeyDown: (view, event) => {
              if (event.key === 'Enter') {
                if (!event.metaKey && !event.ctrlKey) { // 处理仅按下Enter的情况
                  this.handleEnter(event)
                }
              }
            }
          }
        },
      )
      
      if (!this.isCommand) {
        this.editor.view.setProps({
          dispatchTransaction: (tr) => {
            this.editor.view.updateState(this.editor.view.state.apply(tr))
            this.content = this.editor.getArticleInfo()
            this.getMarkdownState()
            this.updateContent()
          },
          editable: () => !this.disabled
        })
        this.updateContent()
        if (this.config.autoFocus) {
          this.focus(true)
        }
      } else {
        this.initCommandEditor(editor_modules)
      }
    },
    initCommandEditor ({ HBEditorModuleBot, HBEditorModuleBotTextTag }) {
      this.editor.modules.bot.parseCommandText(this.content, (data) => {
        if (data.type === 'tag') {
          let label = data.match[1]
          let contets = data.match[2].split(/\s/)
          let [tag_content, ...text] = contets
          let user_regx = /@\{(\d+)\}/g, tag_content_nodes = [], user_matches
          while(user_matches = user_regx.exec(tag_content)) {
            tag_content = tag_content.slice(user_matches.index + user_matches[0].length)
            // 添加文字
            tag_content_nodes.push(HBEditorModuleBot.createTextSpec(tag_content.slice(0, user_matches.index)))
            // 添加用户
            const user = this.insertedUsers[user_matches[1]]
            user && tag_content_nodes.push(HBEditorModuleChatUser.createChatUserSpec(user))
          }
          tag_content_nodes.push(
            HBEditorModuleBot.createTextSpec(tag_content)
          )
          return [
            HBEditorModuleBotTextTag.createTextTagSpec(label, tag_content_nodes),
            HBEditorModuleBot.createTextSpec(text.filter(Boolean).join(' '))
          ]
        }
      })
      this.$emit('commandEditorInited', this.getAllTags())
    },
    focusTag (tag) {
      if (!tag) return
      tag.active()
      tag.focus()
      this.focus(false)
    },
    setOtherDesc (desc) {
      this.editor.modules.bot.setOtherDesc(desc)
    },
    getLinkEditPos (target, position) {
      const rect = target.getBoundingClientRect();
      let top = position === 'bottom' ? rect.bottom + 8 : rect.top - EditLinkHeight - 8
      let left = rect.left + rect.width / 2 - EditLinkWidth / 2
      
      if (left < 0) {
        left = 8
      } else if (left + EditLinkWidth > window.innerWidth) {
        left = window.innerWidth - EditLinkWidth - 8
      }

      if (top < 0) {
        top = rect.bottom + 8
      } else if (top + EditLinkHeight > window.innerHeight) {
        top = rect.top - EditLinkHeight - 8
      }

      return { top, left }
    },
    handleTransformPastedHTML (html) {
      html = htmlToHbEditorHtml(html)
      html = handleH12ToH23(html)
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const imagesTags = Array.from(doc.querySelectorAll('img'));
      const emojis = imagesTags.filter(item => item.getAttribute('data-emoji') == '1')
      const images = imagesTags.filter(item => !emojis.includes(item))

      let hasImage = images.length > 0

      // 不支持图片， 删除所有图片
      if (!this.config.image) {
        images.forEach(img => {
          if (img.getAttribute('data-emoji') !== '1') {
            img.parentNode.removeChild(img)
          }
        })
        // 删除所有.chat-img-html，.chat-img-html是IM里面带了占位符的图片，是个p标签
        const elements = doc.querySelectorAll('.cpt-chat-img.img-info-block');
        elements.forEach(element => {
          element.parentNode.removeChild(element);
        })
      } 

      // 不支持表情， 删除所有表情
      if (!this.config.emoji) {
        emojis.forEach(emoji => {
          emoji.parentNode.removeChild(emoji)
        })
      }
      // console.log('hasImage', hasImage);
      
      if (hasImage) {
        html += '<p></p>'
      }
      return html
    },
    updateContent () {
      this.content = this.isCommand ? this.editor.modules.bot.bot_info.content : this.editor.getArticleInfo()
      this.isEmptyContent = (this.content == '<p></p>') || (this.content == '<h2></h2>') || (this.content == '<h3></h3>') || (this.content == '<ol><li><p></p></li></ol>') || (this.content == '<ul><li><p></p></li></ul>')
      this.$emit('updateContent', this.content, this.isEmptyContent)
    },
    onRichImageAdd (data) {
      return new Promise(async resolve => {
        const imgRes = await this.processPastedImage(data.url)
        if (imgRes === 'error') {
          data.remove()
          resolve()
          return
        }
        this.onImageReceived(imgRes.isCdn ? imgRes.url : imgRes.file, false, imgRes.isCdn).then((url) => {
          data.replaceURL(url)
        }).catch((err) => {
          data.remove()
        }).finally(() => {
          resolve()
        })
      })
    },
    async processPastedImage (url) {
      const type = this.checkImgType(url)
      const result = {
        file: null,
        url: null,
        isCdn: false
      }
      switch (type) {
        case 'base64':
          result.file = base64ToFile(url)
          break
        case 'blob':
          result.file = await blobUrlToFile(url)
          break
        case 'http':
          result.file = await httpUrlToFile(url)
          break
        case 'cdn':
          result.url = url
          result.isCdn = true
          break
        case 'local_file': // 这里需要注意本地开发的时候会报错，线上不会，因为本地开发fetch file://会报错
          if (process.env.IS_CLIENT) {
            result.file = await httpUrlToFile(url)
          } else {
            this.$toast.error('不支持的图片类型')
            return 'error'
          }
          break
        case 'file':
          result.file = url
          break
        default:
          this.$toast.error('不支持的图片类型')
          break
      }
      return result
    },
    checkImgType(imageSource) {
      if (typeof imageSource === 'string') {
        if (imageSource.startsWith('data:image/')) {
          return 'base64'
        } else if (/^http(s)?:\/\//.test(imageSource)) {
          let cdnReg = /^(https|http):\/\/[a-zA-Z0-9]+\.(max-c|maxjia)\.com/
          const isCdnImg = cdnReg.test(imageSource)
          if (isCdnImg) {
            return 'cdn'
          }
          return 'http'
        } else if (imageSource.startsWith('blob:')) {
          return 'blob'
        } else if (imageSource.startsWith('file://')) {
          return 'local_file'
        } else {
          return 'unknown'
        }
      } else if (imageSource instanceof File) {
        return 'file'
      } else {
        return 'unknown'
      }
    },
    onImageReceived(file, insert = false, isCdnImg = false) {      
      return new Promise((resolve, reject) => {
        if (!this.config.image) {
          reject('image not allowed')
          return
        }
        let blobUrl = isCdnImg ? file:  URL.createObjectURL(file)
        let u = new URL(blobUrl)
        let key = u.pathname.slice(u.origin.length + 1)
        let img = new Image()
        img.src = blobUrl
        img.onload = () => {
          let info = new ImageFileInfo()
          info.file = file
          info.width = img.width
          info.height = img.height
          info.blobUrl = blobUrl
          this.handlePasteImage({
            group: this.group,
            type: 'add',
            key,
            value: info
          })
          this.uploadingIds.push(key)
          if (insert) {
            this.editor.modules.chat_img.insertImg({
              url: blobUrl,
              real_url: blobUrl,
              alt: ''
            })
          }
          if (isCdnImg) {
            info.url = file
            info.upload = null
            if (Object.values(this.pastedImageMapper).every(info => !info.upload)) {
              resolve(info.url)
              this.uploadingIds = this.uploadingIds.filter(id => id !== key)
              this.focus()
            }
          } else {
            console.log('ext', file);
            // 获取图片的宽高
            info.upload = uploadFile(
              file,
              {
                extension: file.type.split('/')[1],
                type: 'pic',
                source: 'msg_img'
              }
            ).then(({ url }) => {
              info.url = url
              info.upload = null
              if (Object.values(this.pastedImageMapper).every(info => !info.upload)) {
                resolve(info.url)
              }
            }).catch((err) => {
              this.handlePasteImage({
                group: this.group,
                type: 'delete',
                key,
              })
              reject('upload failed')
            }).finally(() => {
              this.uploadingIds = this.uploadingIds.filter(id => id !== key)
              this.focus()
            })
          }
          
        }
      })
    },
    handleInitValue () {
      if (this.isCommand) {
        return this.initValue
      }
      if (!this.transformToHtml) {
        return handleH12ToH23(this.initValue)
      }
      let content = emojiRender.replaceEmojiByTextName(this.initValue, true)
      return content.split('\n').map(content => {
        if (!content) {
          return `<p></p>`
        } else {
          return `<p>${content}</p>`
        }
      }).join('') || ""
    },
    insertEmoji (data) {
      if (this.disabled || this.isCommand) return
      this.editor.modules.chat_emoji.insertEmoji({
        url: data.url,
        alt: `[${data.type}_${data.icon || data.name}]` // icon对应房间和twemoji，name对应hbEmoji
      })
      this.focus()
    },
    async showPanel(type, data) {
      if (data.is_over) {
        this.showSelectionPanel = false
        this.cancelShowSelectionPanel = true
        this.selectionList = []
        if (this.selectionType === type) {
          this.selectionPos = {}
        }
        return
      }
      this.cancelShowSelectionPanel = false
      this.selectionType = type
      let { head } = this.editor.view.state.selection
      let pos = this.editor.view.coordsAtPos(head)
      this.selectionPos = {
        type,
        top: pos.top + 20,
        left: pos.left - 12,
        ...data
      }
      
      if (data.selectionList?.length) {
        this.selectionList = data.selectionList
      } else if (type === 'at') {
        this.selectionList = await this.getUserList(data.user_name, data.only_type)
        if (this.cancelShowSelectionPanel) {
          return
        }
      } else {
        this.channel_pattern = data.channel
        this.isPanelBottomLock = false
        this.selectionList = await this.getImChannelList(this.channel_pattern)
        if (this.cancelShowSelectionPanel) {
          return
        }
      }
      if (this.selectionList.filter(item => item.type !== 'title').length === 0) {
        this.showPanel(type, { is_over: true })
        return
      }
      this.$nextTick(() => {
        const panel = document.querySelector('.cpt-selection-panel ul')
        if (panel) {
          panel.scrollTop = 0
        }
      })
      const elementWidth = 240
      const elementHeight = getPanelHeight(this.selectionList)
      // 修正元素位置
      // 判断是否超出屏幕右边界
      if (this.selectionPos.left + elementWidth > window.innerWidth) {
        this.selectionPos.left = pos.left - elementWidth + 12; // 放在pos的左侧
      }

      // 判断是否超出屏幕下边界
      if (this.selectionPos.top + elementHeight > window.innerHeight) {
        this.selectionPos.top = pos.top - elementHeight - 12; // 放在pos的上方
      }

      // 判断是否超出屏幕左边界
      if (this.selectionPos.left < 0) {
        this.selectionPos.left = 0; // 保持在屏幕左侧
      }

      // 判断是否超出屏幕上边界
      if (this.selectionPos.top < 0) {
        this.selectionPos.top = 0; // 保持在屏幕顶部
      }
      this.selectionPos.top += 'px'
      this.selectionPos.left += 'px'

      if (this.isCommand) {
        this.notSeclectionToBody = true
        if (data.only_type === 'user') {
          const tag = this.getActiveTag()
          if (tag?.users?.length) {
            this.showPanel(type, { is_over: true })
            return
          }
        }
      } else {
        this.notSeclectionToBody = false
      }
      if (this.notSeclectionToBody) {
        this.selectionPos = {
          position: 'absolute',
          width: '100%',
          left: 0,
          bottom: 'calc(100% + 4px)',
        }
      }
      this.showSelectionPanel = true
      function getPanelHeight (list) {
        if (!list.length) {
          return 80
        }
        let height = 16
        const maxHeight = 316
        list.forEach(item => {
          if (height > maxHeight) {
            return
          }
          if (item.type === 'title') {
            height += 38
          } else {
            height += 36
            if (item.split) {
              height += 15
            }
          }
        })
        return Math.min(height, maxHeight)
      }
    },
    async getUserList(pattern, only_type) {
      let result = []
      pattern = pattern + ''
      if (pattern[0] === '@') {
        pattern = pattern.slice(1)
      }
      if (only_type === 'bot') {
        result = await this.generateBotAtList(pattern)
      } else if (this.channel_data.is_private && only_type !== 'user') {
        result = await this.generatePrivateAtList(pattern)
      } else {
        result = await this.generateAtList(pattern)
      }
      console.log('resultresult', result)
      if (only_type === 'user') {
        result = result.filter(v => v.group === 'user')
      } else if (only_type === 'role') {
        result = result.filter(v => v.group === 'role')
      }
      return result
    },
    async generateAtList (pattern = '') {
      let result = []
      if (this.config.atTypes?.includes('role')) {
        if (this.$permission.judge('ATGroup', this.channel_data.channel_id)) {
          let role_at_meta = []
          let name = '在线成员'
          if (name.startsWith(pattern)) {
            result.unshift({
              label: name,
              group: 'online',
              split: true,
              id: 0,
            })
          }
          role_at_meta = this.cur_roles_list.filter(v => !HIDE_AT_ROLE_TYPE.includes(v.type) && (v.name?.includes(pattern))).map((role) => {
            return {
              label: '@' + role.name,
              group: 'role',
              id: role.id,
              data: role,
              ...this.getAtStyle('role', role)
            }
          })
          result.unshift(...role_at_meta)
        }
      }
      if (this.config.atTypes?.includes('room')) {
        const res = await getMatchedUserList({
          room_id: this.cur_room_data.room_id,
          channel_id: this.channel_data.channel_id,
          type_str: pattern
        })
        if(res.data.status == 'ok') {
          let room_user_list = res.data.result.user_list || []
          room_user_list = room_user_list.map(item => ({
            type: '',
            label: calcUserName(item),
            img: item.avatar,
            id: item.user_id,
            group: 'user',
            data: item,
            ...this.getAtStyle('user', item)
          }))
          result.push(...room_user_list)
        }
        result.unshift({
          type: 'title',
          label: '房间内成员'
        })
      }
      if (this.config.atTypes?.includes('friend')) {
        let my_friends = []
        const { data: { status, result: { friends } } } = await getSimpleFriendList({});
        if (status === "ok") {
          my_friends = friends || [];
        }
        const search_friends = my_friends.filter((v) => {
          return ('' + v.user_id).includes(pattern) || v.nickname.toLowerCase().includes(pattern.toLowerCase()) || v.remarks.toLowerCase().includes(pattern.toLowerCase());
        })
        result = search_friends.map((item) => {
          return {
            type: '',
            label: calcUserName(item),
            img: item.avatar,
            id: item.user_id,
            group: 'user',
            data: item,
            ...this.getAtStyle('user', item)
          }
        })
        result.unshift({
          type: 'title',
          label: '我的好友'
        })
      }
      if (this.config.atTypes?.includes('group')) {
        let groupMembers = []
        const { data: { status, result: groupResult } } = await getGroupChatMemberList({
          chat_group_id: this.cur_group_chat?.chat_group_info?.chat_group_id,
          offset: 0,
          limit: 20,
          search_key: pattern,
        })
        if(status === 'ok') {
          const { group_members } = groupResult
          groupMembers = group_members || []
        }
        result = groupMembers.map(item => ({
          type: '',
          label: calcUserName(item),
          img: item.avatar,
          id: item.heybox_id,
          group: 'user',
          data: item,
          ...this.getAtStyle('user', item)
        }))
        const staticList = [
          {
            label: '全体成员',
            group: 'online',
            split: true,
            id: GROUP_CHAT_ALL_MEMBER,
          },
          {
            type: 'title',
            label: '群聊成员'
          }
        ]
        result = result.filter(v => v.id != this.user_info.user_id)
        result = staticList.concat(result)
      }
      return result
    },
    async generatePrivateAtList (pattern) {
      let role_at_meta = []
      if (this.$permission.judge('ATGroup', this.channel_data.channel_id)) {
        const roles = this.getPriviateRoles()
        role_at_meta = roles.filter(v => !HIDE_AT_ROLE_TYPE.includes(v.type) && (v.name.includes(pattern))).map((role) => {
          return {
            label: role.name,
            group: 'role',
            id: role.id,
            data: role,
            ...this.getAtStyle('role', role)
          }
        })
      }
      const users = this.channel_data.private_users || []
      const room_user_list = users.map(item=>({
        type: '',
        label: calcUserName(item),
        img: item.avatar,
        id: item.user_id,// '@{123123}'
        group: 'user',
        data: item,
        ...this.getAtStyle('user', item)
      }))
      return [...role_at_meta, ...room_user_list].filter(v => v.id)
    },
    async generateBotAtList (pattern) {
      pattern = pattern.toLowerCase()
      const pattern_bot_list = this.cur_room_bots.filter(item => 
        item.bot_account_info.nickname.toLowerCase().includes(pattern) &&
        item.bot_account_info.user_id !== -1 && 
        item.bot_account_info.nickname.toLowerCase() !== pattern
      )
      const bot_list = pattern_bot_list.map(item => {
        return {
          type: '',
          label: item.bot_account_info.nickname,
          img: item.bot_account_info.avatar,
          id: item.bot_account_info.user_id,
          group: 'bot',
          data: item,
        }
      })
      return bot_list
    },
    async getImChannelList(pattern = '', anchor_channel_id) {
      let res = anchor_channel_id ? [] : [{
        type: 'title',
        label: '文字频道'
      }]
      let channels = []
      const limit = 10
      if (this.cur_room_data_is_big) {
        const { data } = await getFullChannelList({
          room_id: this.cur_room_data.room_id,
          include_channel_types: [CHANNEL_TYPES.text, CHANNEL_TYPES.notice],
          is_password: false,
          is_private: false,
          keyword: pattern,
          limit,
          anchor_channel_id
        })
        if (data.status !== 'ok') {
          return res
        }
        channels = data.result.channels || []
        if (channels.length.length === 0) {
          return []
        }
      } else {
        channels = this.channels
      }
      
      const formatResItem = (item) => {
        return {
          label: item.channel_name,
          id: item.channel_id,
          data: item,
          group: 'channel',
        }
      }
      let startIndex = 0
      if (anchor_channel_id && !this.cur_room_data_is_big) {
        startIndex = channels.findIndex(v => v.channel_id === anchor_channel_id) + 1
      }
      let count = 0
      for (let i = startIndex; i < channels.length; i++) { 
        const item = channels[i]
        let res_item = null
        if (!this.cur_room_data_is_big) {
          if(!item.not_show && CHANNEL_TYPES.only_text_types.includes(item.channel_type) && !item.is_private && !item.password && item.channel_name.indexOf(pattern) > -1) {
            res_item = formatResItem(item)
          }
        } else {
          res_item = formatResItem(item)
        }
        if (res_item) {
          count++
          res.push(res_item)
        }
        if (!this.cur_room_data_is_big && count >= limit) { 
          break
        }
      }
      return res
    },
    async handleSelectionScrollBottom () {
      this.isPanelBottomLock = true
      const last_item = this.selectionList[this.selectionList.length - 1]
      const anchor_channel_id = last_item && last_item.group === 'channel' ? last_item.data.channel_id : undefined
      const list = await this.getImChannelList(this.channel_pattern, anchor_channel_id)
      this.isPanelBottomLock = false
      if (this.cancelShowSelectionPanel) {
        return
      }
      if (list.length > 0) {
        const last_item2 = this.selectionList[this.selectionList.length - 1]
        if (last_item2 && last_item2.group === 'channel' && last_item2.data.channel_id === anchor_channel_id) {
          this.selectionList = this.selectionList.concat(list)
        } 
      } else {
        this.isPanelBottomLock = true
      }
    },
    handleSelectionSelect (item) {
      const { data } = item
      const isUserAt = ['user', 'role', 'online', 'bot'].includes(item.group)
      let { start, end } = this.selectionPos
      const activeTag = this.getActiveTag()
      if (item.group === 'optional_menu') {
        this.editor.modules.bot_text_tag.insertBotTextTag(item.label).then(tag => {
          if (BOT_COMMAND_OPTION_TYPES_NEED_UPLOAD.includes(data.type)) {
            this.editor.modules.bot_text_tag.toggleEditable(tag)
          }
          this.focusTag(tag)
        })
      } else if (item.group === 'choice') {
        activeTag.setContent(item.label)
      } else if (isUserAt) {
        let inner = ''
        if (item.group === 'online') {
          inner = item.label
        } else if (item.group === 'bot') {
          inner = data.bot_account_info.nickname
        } else {
          const name = item.group === 'user' ? calcUserName(data) : data.name
          inner = getEmojiHtml(name, { width: '14px', 'margin-left': '2px' })
        }
        if (this.isCommand && activeTag) {
          activeTag.setContent('')
          const user_map = {
            remind_user_id: item.id,
            inner: '@' + inner,
            class_name: `${item.class_name || ''}${item.group === 'role' ? ' role-at' : ''}`,
            style: item.style || '',
          }
          this.insertedUsers[user_map.remind_user_id] = user_map
          this.editor.modules.chat_user.insertUser(user_map)
        } else {
          this.editor.modules.chat_user.replaceUser(start, end + 1, {
            remind_user_id: item.id,
            inner: '@' + inner,
            class_name: `${item.class_name || ''}${item.group === 'role' ? ' role-at' : ''}`,
            style: item.style || '',
          })
        }
      } else {
        // todo: 支持频道
        if (this.isCommand && activeTag) {
          activeTag.setContent('#' + data.label)
        } else {
          this.editor.modules.chat_channel.replaceChannel(start, end + 1, {
            remind_channel_id: data.channel_id,
            inner: '#' + data.channel_name,
            style: ''
          })
        }
      }
      this.handleSelectionPanelClose()
      this.$nextTick(() => {
        this.focus()
      })
    },
    insertAtUser (user) {
      if (user) {
        const name = getEmojiHtml(calcUserName(user), { width: '14px', 'margin-left': '2px' })
        const user_map = {
          remind_user_id: user.user_id,
          inner: '@' + name,
          ...this.getAtStyle('user', user)
        }
        this.insertedUsers[user_map.remind_user_id] = user_map
        this.editor.modules.chat_user.insertUser(user_map)
      }
      this.focus(true)
    },
    getAtStyle (type = 'user', data) {
      let color = null
      if (type === 'user') {
        color = calcUserRoleColor(data.roles, 'var(--brand-text)')
      } else if (type === 'role') {
        if (data?.color_list?.length === 2) {
          color = data.color_list.map(c => decimalToHexColor(c))
        } else {
          color = decimalToHexColor(data.color)
        }
      }
      let is_gradient = false
      if (Array.isArray(color) && color.length === 2) {
        is_gradient = true
        color = `linear-gradient(90deg, ${color[0]} 0%, ${color[1]} 100%)`
      }
      return {
        class_name: is_gradient ? 'gradient' : '',
        style: is_gradient ? `background-image: ${color};` : `color: ${color};`
      }
    },
    handleSelectionPanelClose () {
      this.showSelectionPanel = false
      this.selectionList = []
      if (!this.notSeclectionToBody) {
        const panel = document.querySelector('.cpt-selection-panel')
        panel && panel.remove()
      }
    },
    getActiveTag () {
      return this.getAllTags().find(tag => tag.is_active)
    },
    getAllTags () {
      if (!this.isCommand) return []
      return this.editor.modules?.bot?.bot_info?.tags || []
    },
    applyFormat ({group, type, value}) {
      if (!this.checkSameGroup(group)) return
      switch (type) {
        case 'h1':
          this.editor.modules.heading.toggleMainTitle(value)
          break
        case 'h2':
          this.editor.modules.heading.toggleSubTitle(value) 
          break
        case 'bold':
          this.editor.modules.paragraph.blodText(value)
          break
        case 'italic':
          this.editor.modules.paragraph.EmText(value)
          break
        case 'underline':
          this.editor.modules.paragraph.underlineText(value)
          break
        case 'code':
          this.editor.modules.paragraph.codeText(value)
          break
        case 'ol':
          this.editor.modules.list.toggleOrderList(value)
          break
        case 'ul':
          this.editor.modules.list.toggleUnorderList(value)
          break
      }
      this.getMarkdownState()
      this.focus()
    },

    addImageToEditor (blobUrl, loadCb) {
      let img = new Image()
      img.src = blobUrl
      img.onload = () => {
        this.editor.modules.chat_img.insertImg({
          url: blobUrl,
          real_url: blobUrl,
          alt: ''
        })
        this.editor.commands.enter()
        loadCb && loadCb()
      }
    },
    
    handleToolBarClick ({group, type, data, cb, is_hidden, position = 'top'}) {
      if (!this.checkSameGroup(group)) return
      if (type === 'image') {
        if (!this.config.image) {
          return
        }
        const file = data.event.target.files[0]
        let blobUrl = URL.createObjectURL(file)
        this.addImageToEditor(blobUrl, () => {
          data.event.target.value = ''
        })
      } else if (type === 'link') {
        if (is_hidden) {
          this.editLinkDialogConfig = {}
          return
        }
        const { top, left } = this.getLinkEditPos(data.event.target, position)
        this.editLinkDialogConfig = {
          show: true,
          top,
          left,
          text: '',
          link: '',
          is_add: true,
          closeCb: cb,
          target: data.event.target
        }
      }
    },
    getMarkdownState () {
      if (!this.config.markdown) return
      const state = {
        h1: this.editor.modules.heading?.active_state.h2,
        h2: this.editor.modules.heading?.active_state.h3,
        bold: this.editor.modules.paragraph.active_blod,
        italic: this.editor.modules.paragraph.active_em,
        underline: this.editor.modules.paragraph.active_u,
        code: this.editor.modules.paragraph.active_code,
        ol: this.editor.modules.list?.active.ol,
        ul: this.editor.modules.list?.active.ul,
        emoji: false,
        link: false,
      }
      this.$emit('updateToolbar', state)
    },
    replyUser (data) {
      this.ensureHbEditorLoaded().then(() => {
        if (data) {
          const name = getEmojiHtml(calcUserName(data), { width: '14px', 'margin-left': '2px' })
          this.editor.modules.chat_user.replyUser({
            remind_user_id: data.user_id,
            inner: '@' + name,
            ...this.getAtStyle('user', data)
          })
        }
        this.focus(true)
      })
    },
    cancelReplyUser () {
      this.editor.modules.chat_user.cancelReplyUser()
      this.focus(true)
    },
    handleLinkUpdate(values) {
      let [text, link] = values
      if (!this.editLinkDialogConfig.show) {
        return
      }
      if (!text) {
        text = link
      }
      // 如果link不是以http或者https或者//开头，那么加上https://
      if (link && !link.startsWith('http') && !link.startsWith('//')) {
        link = 'https://' + link
      }
      
      link = replaceAngleBracket(link)

      if (this.editLinkDialogConfig.is_add) {
        this.editor.modules.link.insertLink({
          href: link,
          text: text || link
        })
      } else {
        const old_link = this.editLinkDialogConfig.link
        const old_text = this.editLinkDialogConfig.text
        if (text != old_text || link != old_link) {
          const { view, node, nodePos } = this.editLinkDialogConfig
          let { tr } = view.state
          tr.replaceWith(nodePos, nodePos + node.nodeSize, node.type.createAndFill({
            ...node.attrs,
            href: link,
            text: text
          }, text ? view.state.schema.text(text): []));
          view.dispatch(tr)
        }
      }
      this.editLinkDialogConfig.closeCb && this.editLinkDialogConfig.closeCb()
      this.editLinkDialogConfig = {}
    },
    handleCloseEditLinkDialog(event, foceClose = false) {
      if (!this.editLinkDialogConfig.show) return
      // 检测点击的位置是否在输入框内
      if (!foceClose) {
        const rect = this.editLinkDialogConfig.target.getBoundingClientRect()
        if (event.pageX >= rect.left && event.pageX <= rect.right && event.pageY >= rect.top && event.pageY <= rect.bottom) {
          return
        }
      }
      this.editLinkDialogConfig.closeCb && this.editLinkDialogConfig.closeCb()
      this.editLinkDialogConfig = {}
    },
    handleEnter (e) {
      const show = this.showSelectionPanel
      if (this.showSelectionPanel) {
        this.$refs['selectionPanel'].handleKeydown(e)
      }
      // 这里必须用show，因为此时this.showSelectionPanel已经变了
      this.$emit('enter', e, show)
    },
    checkSameGroup (group) {
      return this.group === group
    },
    // mode?: "only_insert" | "relace_all"
    // at?: "start" | "end" | "selection"
    setInput (val = '', options = {mode: "relace_all", at: "end"}) {
      if (this.disabled) return
      this.ensureHbEditorLoaded().then(() => {
        this.editor?.commands?.insertContentWithHTML(val, options)
        this.focus()
      })
    },
    clearInput () {
      this.setInput('')
    },
    focus (end = false) {
      if (this.disabled) return
      this.ensureHbEditorLoaded().then(() => {
        if (end) {
          this.editor.setCursor()
        } else {
          this.editor.view.focus()
        }
      })
    },
    handleUpdateActiveTagContent({ group, name, content }){
      if(group !== this.group) return

      const tags = this.getAllTags()
      const changeTag = tags.find(tag => tag.label === name)
      if (changeTag) {
        changeTag.setContent(content)
        this.editor.modules.bot_text_tag.toggleEditable(changeTag)
        changeTag.normal()
        this.focus(true)
      } else {
        const currActiveTag = this.getActiveTag()
        currActiveTag && currActiveTag.normal()
        this.focus(true)
        this.editor.modules.bot_text_tag.insertBotTextTag(name).then(tag => {
          this.focusTag(tag)
          tag.setContent(content)
          this.editor.modules.bot_text_tag.toggleEditable(tag)
          tag.normal()
        })
      }
    },
  }
}
</script>

<style lang="scss">
.cpt-rich-text-editor {
  max-width: 100%;
  width: 100%;
  padding: 0 8px 0 16px;
  position: relative;
  .hb-editor-wrapper {
    width: 100%;
    height: 100%;
    overflow-y: scroll;
    &.command-editor {
      br {
        display: none !important;
      }
    }
    .hb-editor {
      width: 100%;
      height: 100%;
      color: $d-op-5;
      outline: none;
      font-size: 14px;
      line-height: 24px;
      overflow-wrap: break-word;
      ol {
        list-style: decimal;
      }
      ul {
        list-style: disc;
      }
      p {
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
      }
      code {
        margin: 0 4px;
        border-radius: 3px;
        border: 1px solid $d-op-2;
        background: $d-op-1;
        padding: 2px 4px;
        font-size: 13px;
        line-height: 18px;
        color: $d-op-5;
      }
      .hb-chat-inline-emoji {
        margin-top: -2px;
        width: 22px;
        height: 22px;
        display: inline;
        vertical-align: middle;
        user-select: all;
      }
      .hb-editor-chat-user {
        cursor: pointer;
        color: $d-bd-tx;
        user-select: all;
        // padding-right: 4px;
        &.gradient {
          color: transparent;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        &.role-at {
          cursor: default;
        }
      }
      .hb-editor-chat-channel {
        color: $d-bd-tx;
        user-select: all;
      }
      .hb-editor-chat-img {
        max-width: 328px;
        max-height: 400px;
        object-fit: contain;
        pointer-events: all;
        &.ProseMirror-selectednode {
          outline: 1px solid $d-bd-tx;
        }
      }
      .command-name {
        color: $d-op-5;
      }
      .tag-wrapper {
        display: inline-flex;
        align-items: center;
        border-radius: 4px;
        border: 1px solid transparent;
        margin: 0 6px;
        line-height: 24px;
        height: 24px;
        color: $d-op-5;
        max-width: calc(100% - var(--tag-name-width, 12px));
        &.active {
          border-color: $d-op-4;
        }
        &.warn {
          border-color: $d-er-tx;
        }
        .tag-name {
          height: 100%;
          padding: 0 6px 2px;
          font-size: 14px;
          line-height: 22px;
          background: $d-op-s4;
          border-radius: 4px 0 0 4px;
          user-select: all;
          pointer-events: none;
          white-space: nowrap;
        }
        .tag-value {
          display: inline;
          padding: 3px 6px;
          font-size: 14px;
          line-height: 16px;
          background: $d-op-1;
          border-radius: 0 3px 3px 0;
          white-space: nowrap;
          overflow: hidden;
        }
        &:first-child {
          margin-left: 0;
        }
        // &:last-child {
        //   margin-right: 0;
        // }
      }
      .command-desc {
        position: relative;
        display: inline;
        width: 0;
        font-size: 14px;
        font-weight: 400;
        color: $d-op-3;
        .command-desc-text {
          position: absolute;
          top: 50%;
          left: 0;
          white-space: nowrap;
          transform: translate3d(0, -50%, 0);
          transform-origin: left top;
        }
      }
      img.ProseMirror-separator {
        display: inline !important;
        border: none !important;
        margin: 0 !important;
        display: none;
      }
      a {
        color: $d-bd-tx;
        cursor: pointer;
        &:hover {
          text-decoration: underline;
        }
      }
      h2 {
        line-height: 32px;
        font-size: 20px;
        margin: 16px 0 8px;
        &:first-child {
          margin-top: 0;
          &:last-child {
            margin: 0;
          }
        }
        .hb-chat-inline-emoji {
          width: 28px !important;
          height: 28px !important;
          margin: -6px 1px;
        }
      }

      h3 {
        line-height: 29px;
        font-size: 17px;
        margin: 16px 0 8px;
        &:first-child {
          margin-top: 0;
          &:last-child {
            margin: 0;
          }
        }
        .hb-chat-inline-emoji {
          width: 27px !important;
          height: 27px !important;
          margin: -6px 1px;
        }
      }

      h3,h4,h5,h6 {
        line-height: 24px;
        font-size: 16px;
      }
    }
  }
  .placeholder {
    position: absolute;
    left: 16px;
    top: 8px;
    z-index: 1;
    width: 100%;
    pointer-events: none;
    color: $d-op-3;
    font-size: 14px;
    letter-spacing: 0.04em;
  }
  .max-num-wrapper {
    position: absolute;
    bottom: 0;
    right: 0;
    color: $d-tx-3;
    font-family: Roboto;
    font-size: 14px;
    font-weight: 500;
    z-index: 2;
    &.overflow {
      color: $d-er-tx;
    }
  }
}
</style>